"use client"

import { useCallback, useEffect, useId, useState } from "react"
import { CircleHelp, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { ModalContent, ModalOverlay } from "@/components/ui/modal-shell"
import { cn } from "@/lib/utils"

function GuideSections() {
  return (
    <div className="space-y-6 text-left">
      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">What is a recurring event</h3>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>
            A recurring event is a program (like Sunday Worship) that repeats over time.
          </li>
          <li>Each date becomes its own chapter with its own photos and details.</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">How creation works</h3>
        <p className="type-body-sm font-medium text-foreground">For users</p>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>Create event → toggle recurring → choose schedule</li>
          <li>That creates a series (program) + first chapter</li>
        </ul>
        <p className="type-body-sm font-medium text-foreground">For church/admin</p>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>
            Example: “Sunday Worship (every Sunday)” → April 20 = Chapter 1 → April 27 =
            Chapter 2
          </li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">Chapters (important)</h3>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>Each week = one chapter</li>
          <li>Photos and videos added that week stay in that chapter only</li>
          <li>Nothing gets overwritten</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">Media (photos &amp; videos)</h3>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>Uploads are tied to that specific day</li>
          <li>Next week&apos;s uploads go to the next chapter</li>
          <li>Members don&apos;t re-add—everything stays in place</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">Member experience</h3>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>Save once (follow program)</li>
          <li>Open anytime to see the latest week</li>
          <li>Scroll to see past weeks (story)</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">Admin experience (church)</h3>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>Edit each week separately</li>
          <li>Add title, notes, photos for that specific week</li>
          <li>Past weeks stay unchanged</li>
          <li>New week = new chapter automatically</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">Story (archive)</h3>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>All chapters stack into one story</li>
          <li>Newest week at top</li>
          <li>Past weeks below as history</li>
        </ul>
      </section>

      <section className="space-y-2">
        <h3 className="type-card-title text-foreground">Public sharing (church)</h3>
        <ul className="type-body-sm list-disc space-y-1.5 pl-4 text-muted-foreground">
          <li>Share one link for the whole program</li>
          <li>Members and visitors can see current and past weeks</li>
          <li>Story grows over time</li>
        </ul>
      </section>

      <section className="space-y-3 rounded-xl border border-border/50 bg-muted/15 p-4">
        <h3 className="type-card-title text-foreground">Simple example</h3>
        <p className="type-body-sm font-medium text-foreground">Sunday Worship</p>
        <ul className="type-body-sm list-disc space-y-1 pl-4 text-muted-foreground">
          <li>April 20 → photos, sermon, notes</li>
          <li>April 27 → new photos, new content</li>
          <li>May 4 → new content</li>
        </ul>
        <p className="type-body-sm text-muted-foreground">
          Each one is separate. Together they form the story.
        </p>
      </section>
    </div>
  )
}

export type RecurringGuideModalProps = {
  open: boolean
  onClose: () => void
}

/**
 * Controlled modal for the recurring-events guide. Use from sheets/menus that need
 * an explicit open state (e.g. Repeat picker + Guide row).
 */
export function RecurringGuideModal({ open, onClose }: RecurringGuideModalProps) {
  const titleId = useId()
  const onKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    },
    [onClose],
  )

  useEffect(() => {
    if (!open) return
    document.addEventListener("keydown", onKey)
    document.body.style.overflow = "hidden"
    return () => {
      document.removeEventListener("keydown", onKey)
      document.body.style.overflow = ""
    }
  }, [open, onKey])

  if (!open) return null

  return (
    <ModalOverlay
      className="z-[210] flex items-end justify-center p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <ModalContent
        id={titleId}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${titleId}-heading`}
        className="flex max-h-[min(88dvh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-border/60 bg-background shadow-lg sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/40 px-4 py-3 sm:px-5">
          <h2
            id={`${titleId}-heading`}
            className="type-card-title pr-2 text-foreground"
          >
            How recurring events work
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Close"
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <div
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-6 pt-2 [-webkit-overflow-scrolling:touch] sm:px-5"
        >
          <GuideSections />
        </div>
      </ModalContent>
    </ModalOverlay>
  )
}

export type RecurringGuideTriggerProps = {
  /** `story`: icon-only for dark story chrome. `inline`: text link. `create`: compact chip. */
  variant?: "default" | "inline" | "story" | "create"
  className?: string
}

export function RecurringGuideTrigger({
  variant = "default",
  className,
}: RecurringGuideTriggerProps) {
  const [open, setOpen] = useState(false)
  const onClose = useCallback(() => setOpen(false), [])

  return (
    <>
      {variant === "story" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-border/35 bg-background/10 text-text-secondary backdrop-blur-sm transition-colors hover:border-border/50 hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
          aria-label="How recurring programs work"
          aria-expanded={open}
        >
          <CircleHelp className="size-4" aria-hidden />
        </button>
      ) : variant === "inline" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "type-caption shrink-0 text-primary underline-offset-4 hover:underline",
            className,
          )}
          aria-expanded={open}
        >
          How it works
        </button>
      ) : variant === "create" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "type-caption inline-flex items-center gap-1.5 rounded-full border border-border/45 bg-muted/20 px-3 py-1.5 text-muted-foreground transition-colors hover:border-border/60 hover:text-foreground",
            className,
          )}
          aria-expanded={open}
        >
          <CircleHelp className="size-3.5 shrink-0 opacity-80" aria-hidden />
          How it works
        </button>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={cn("h-8 gap-1.5 px-2 text-muted-foreground", className)}
          onClick={() => setOpen(true)}
          aria-expanded={open}
        >
          <CircleHelp className="size-4 shrink-0" aria-hidden />
          How it works
        </Button>
      )}

      <RecurringGuideModal open={open} onClose={onClose} />
    </>
  )
}
